import React from "react";
import { Tooltip } from "@heroui/react";
import { Typography } from "../../../../../../components";
import { useTranslations } from "../../../../../../contexts/translation.context";
import {
  UserCheckIc,
  GroupUserIc,
  LinkIcon,
  ChartSquare,
  ArrowGreen
} from '../../../../../../components/icons';
import { OrderData } from "./utils";
import OrderStatsTooltip from "./OrderStatsTooltip";

interface CampaignCellProps {
  order: OrderData;
}

const CampaignCell: React.FC<CampaignCellProps> = ({ order }) => {
  const { t } = useTranslations();

  return (
    <div className="flex flex-col gap-[4px]">
      <Tooltip
        content={<OrderStatsTooltip statistics={order.campaign.statistics} />}
        className="p-[16px]"
        >
        <div className="flex items-center gap-2 cursor-pointer">
          <UserCheckIc className="text-gray-600" />
          <Typography variant="body3" className="text-gray-900">
            {order.campaign.statistics.verifiedUsers}/{order.campaign.statistics.totalUsers}
          </Typography>
          <GroupUserIc className="text-gray-600" />
          <Typography variant="body3" className="text-gray-900">
            {order.campaign.statistics.verifiedUsers}
          </Typography>
          <ChartSquare/>
        </div>
      </Tooltip>
      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-[#71717A]">
          {t('orders.campaign.transactions')}:
        </Typography>
        <Typography variant="body2" className="font-semibold">
          ${order.campaign.statistics.transactions.total.toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-[#71717A]">
          {t('orders.campaign.cost')}:
        </Typography>
        <Typography variant="body2" className="font-semibold">
          ${(typeof order.campaign.statistics.cost === 'number' ? order.campaign.statistics.cost : parseFloat(order.campaign.statistics.cost) || 0).toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-[#71717A]">
          {t('orders.campaign.arpu')}:
        </Typography>
        <Typography variant="body2" className="font-semibold">
          ${(typeof order.campaign.statistics.arpu === 'number' ? order.campaign.statistics.arpu : parseFloat(order.campaign.statistics.arpu) || 0).toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-[#71717A]">
          {t('orders.campaign.arpuUsd')}:
        </Typography>
        <Typography variant="body2" className="font-semibold">
          ${(typeof order.campaign.statistics.arpuUsd === 'number' ? order.campaign.statistics.arpuUsd : parseFloat(order.campaign.statistics.arpuUsd) || 0).toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-[#71717A]">
          {t('orders.campaign.romi')}:
        </Typography>
        <Typography variant="body2" className="font-semibold">
          {(typeof order.campaign.statistics.romi === 'number' ? order.campaign.statistics.romi : parseFloat(order.campaign.statistics.romi) || 0).toFixed(2)}%
        </Typography>
      </div>

      <div className="flex items-center gap-[4px] bg-green-100 text-green-800 rounded-lg px-[8px] py-[4px] w-fit h-[24px]">
        <ArrowGreen/>
        <Typography variant="body3" className="text-green-800">
          {order.campaign.transitions_count} ({order.campaign.transitions_percentage})
        </Typography>
      </div>

      {order.campaign.link && (
        <a href={order.campaign.link} target="__blank" className="flex items-center gap-[4px] bg-[#E0EDFC] rounded-full px-2 py-1">
          <LinkIcon size={18} fill='#006FEE' className="text-[#006FEE]" />
          <Typography variant="body2" className="!text-[#006FEE] truncate max-w-48">
            {order.campaign.link}
          </Typography>
        </a>
      )}
    </div>
  );
};

export default CampaignCell;
